import api from './axiosConfig';

// ══════════════════════════════════════════════════════════════
// Tipos
// ══════════════════════════════════════════════════════════════

export type CodigoTipoNegocio = 'GENERAL' | 'COMIDAS_RAPIDAS' | 'RESTAURANTE' | 'CAFETERIA';

export interface TipoNegocio {
  codigo: CodigoTipoNegocio;
  nombre: string;
  descripcion?: string;
  icono?: string;
  pantallaInicio: 'CAJA' | 'MESAS';
  plantillaMercado?: string;
  modulosExtra: string[];
}

export interface ContextoNegocio {
  empresaId: number;
  tipoNegocio: CodigoTipoNegocio;
  tipoNegocioNombre?: string;
  pantallaInicio: 'CAJA' | 'MESAS';
  modulos: string[];
  catalogoModulos: { codigo: string; nombre: string; descripcion?: string; activo: boolean }[];
  restaurante: {
    propinaSugeridaPct: number;
    propinaObligatoria: boolean;
    comandaAutomatica: boolean;
    descontarRecetas: boolean;
  };
}

export type TipoAccionModificador = 'NOTA' | 'QUITAR' | 'AGREGAR';

export interface OpcionModificador {
  opcionModificadorId?: number;
  nombre: string;
  tipoAccion: TipoAccionModificador;
  precioAdicional: number;
  productoInsumoId?: number | null;
  cantidadInsumo: number;
  porDefecto: boolean;
  orden?: number;
  insumoNombre?: string | null;
  insumoUnidad?: string | null;
}

export interface GrupoModificador {
  grupoModificadorId: number;
  empresaId: number;
  nombre: string;
  descripcion?: string;
  minSeleccion: number;
  maxSeleccion: number;
  orden: number;
  activo: boolean;
  productosAsignados: number;
  opciones: OpcionModificador[];
}

/** Modificador elegido en una línea de venta / cuenta. */
export interface ModificadorItem {
  opcionModificadorId?: number;
  nombreGrupo: string;
  nombreOpcion: string;
  tipoAccion: TipoAccionModificador;
  precioAdicional: number;
}

export interface ProductoBuscado {
  productoId: number;
  codigo: string;
  nombre: string;
  tipoProducto: string;
  costo: number;
  precioVenta: number;
  stockActual: number;
  controlaInventario: boolean;
  visibleEnPOS: boolean;
  enviaACocina: boolean;
  estacionCocina?: string;
  unidad: string;
}

export interface ComponenteReceta {
  comboDetalleId?: number;
  productoItemId: number;
  cantidad: number;
  orden?: number;
  esOpcional?: boolean;
  nombre?: string;
  codigo?: string;
  tipoProducto?: string;
  unidad?: string;
  costoUnitario?: number;
  costo?: number;
  stockActual?: number;
  controlaInventario?: boolean;
}

export interface RecetaResumen {
  productoId: number;
  codigo: string;
  nombre: string;
  tipoProducto: string;
  precioVenta: number;
  enviaACocina: boolean;
  estacionCocina?: string;
  imagenUrl?: string;
  componentes: number;
  costoReceta: number;
  margenPct?: number | null;
}

export interface RecetaDetalle {
  productoId: number;
  codigo: string;
  nombre: string;
  tipoProducto: string;
  precioVenta: number;
  enviaACocina: boolean;
  estacionCocina?: string;
  tiempoPreparacionMin?: number | null;
  diasDisponibles?: number | null;
  costoReceta: number;
  margenPct?: number | null;
  componentes: ComponenteReceta[];
}

export type EstadoComanda = 'PENDIENTE' | 'EN_PREPARACION' | 'LISTA' | 'ENTREGADA' | 'ANULADA';

export interface ComandaItem {
  comandaItemId: number;
  productoId: number;
  nombreProducto: string;
  cantidad: number;
  modificadores?: string;
  notas?: string;
  estado: EstadoComanda;
}

export interface Comanda {
  comandaId: number;
  numeroDia: number;
  origen: 'VENTA' | 'CUENTA' | 'PEDIDO';
  ventaId?: number;
  cuentaId?: number;
  mesaId?: number;
  mesa?: string;
  referencia?: string;
  ronda?: number;
  estacion: string;
  paraLlevar: boolean;
  estado: EstadoComanda;
  notas?: string;
  fechaCreacion: string;
  fechaInicio?: string;
  fechaLista?: string;
  fechaEntregada?: string;
  minutosEspera: number;
  items: ComandaItem[];
}

export interface ZonaMesa { zonaMesaId: number; nombre: string; orden: number; }

export type EstadoMesa = 'LIBRE' | 'OCUPADA' | 'POR_COBRAR' | 'RESERVADA';
export type FormaMesa = 'CUADRADA' | 'REDONDA' | 'RECTANGULAR' | 'BARRA';

export interface MesaMapa {
  mesaId: number;
  zonaMesaId?: number | null;
  numero: string;
  nombre?: string;
  capacidad: number;
  forma: FormaMesa;
  posX: number;
  posY: number;
  estado: EstadoMesa;
  cuenta?: {
    cuentaId?: number;
    unida: boolean;
    mesaPrincipal?: string;
    numero?: number;
    numeroPersonas?: number;
    meseroNombre?: string;
    estado?: string;
    total: number;
    sinEnviar: number;
    minutos: number;
  } | null;
}

export interface MapaMesas { zonas: ZonaMesa[]; mesas: MesaMapa[]; }

export type EstadoCuentaItem = 'PENDIENTE' | 'ENVIADO' | 'PAGADO' | 'ANULADO';

export interface CuentaItem {
  cuentaItemId: number;
  ronda: number;
  productoId: number;
  productoVarianteId?: number;
  nombreProducto: string;
  cantidad: number;
  precioUnitario: number;
  porcentajeDescuento: number;
  total: number;
  modificadores?: ModificadorItem[] | null;
  notas?: string;
  comensal?: number | null;
  estado: EstadoCuentaItem;
  /** Estado en la pantalla de cocina (solo ítems enviados que van a cocina). */
  estadoCocina?: EstadoComanda | null;
  comandaId?: number;
  ventaId?: number;
  fechaCreacion: string;
}

export interface CuentaDetalle {
  cuentaId: number;
  empresaId: number;
  sucursalId: number;
  almacenId: number;
  mesaId?: number | null;
  mesa?: string | null;
  mesasUnidas: string[];
  numero: number;
  nombre?: string | null;
  numeroPersonas: number;
  meseroId: string;
  meseroNombre?: string;
  clienteId?: number | null;
  estado: 'ABIERTA' | 'POR_COBRAR' | 'CERRADA' | 'ANULADA' | 'UNIDA';
  rondaActual: number;
  notas?: string;
  fechaApertura: string;
  fechaCierre?: string;
  items: CuentaItem[];
  totalPorPagar: number;
  totalPagado: number;
  sinEnviar: number;
  propinaSugeridaPct: number;
  propinaObligatoria: boolean;
}

export interface CuentaResumen {
  cuentaId: number;
  numero: number;
  nombre?: string;
  mesaId?: number;
  mesa?: string;
  numeroPersonas: number;
  meseroNombre?: string;
  estado: string;
  fechaApertura: string;
  rondaActual: number;
  total: number;
}

export interface NuevoItemCuenta {
  productoId: number;
  productoVarianteId?: number;
  cantidad: number;
  precioUnitario: number;
  porcentajeDescuento?: number;
  notas?: string;
  comensal?: number;
  modificadores?: ModificadorItem[];
  enviarYa?: boolean;
}

// ══════════════════════════════════════════════════════════════
// Servicios
// ══════════════════════════════════════════════════════════════

const d = <T,>(p: Promise<{ data: T }>) => p.then(r => r.data);

export const negocioService = {
  tipos: () => d<TipoNegocio[]>(api.get('/pos/negocio/tipos')),
  contexto: (empresaId: number) => d<ContextoNegocio>(api.get(`/pos/negocio/${empresaId}`)),
  cambiarTipo: (empresaId: number, tipoNegocio: CodigoTipoNegocio) =>
    d<ContextoNegocio>(api.put(`/pos/negocio/${empresaId}/tipo`, { tipoNegocio })),
  ajustarModulos: (empresaId: number, modulos: { codigo: string; activo: boolean }[]) =>
    d<ContextoNegocio>(api.put(`/pos/negocio/${empresaId}/modulos`, modulos)),
  configRestaurante: (empresaId: number, cfg: ContextoNegocio['restaurante']) =>
    d<ContextoNegocio>(api.put(`/pos/negocio/${empresaId}/restaurante`, cfg)),
};

export const modificadoresService = {
  listar: (empresaId: number, incluirInactivos = false) =>
    d<GrupoModificador[]>(api.get('/pos/modificadores', { params: { empresaId, incluirInactivos } })),
  porProducto: (empresaId: number) =>
    d<Record<string, GrupoModificador[]>>(api.get('/pos/modificadores/por-producto', { params: { empresaId } })),
  crear: (data: Partial<GrupoModificador> & { empresaId: number }) =>
    d<GrupoModificador>(api.post('/pos/modificadores', data)),
  actualizar: (id: number, data: Partial<GrupoModificador> & { empresaId: number }) =>
    d<GrupoModificador>(api.put(`/pos/modificadores/${id}`, data)),
  desactivar: (id: number) => api.delete(`/pos/modificadores/${id}`),
  deProducto: (productoId: number) => d<GrupoModificador[]>(api.get(`/pos/modificadores/producto/${productoId}`)),
  asignarAProducto: (productoId: number, grupoIds: number[]) =>
    d<GrupoModificador[]>(api.put(`/pos/modificadores/producto/${productoId}`, grupoIds)),
};

export const recetasService = {
  listar: (empresaId: number, buscar?: string) =>
    d<RecetaResumen[]>(api.get('/pos/recetas', { params: { empresaId, buscar } })),
  detalle: (productoId: number) => d<RecetaDetalle>(api.get(`/pos/recetas/${productoId}`)),
  guardar: (productoId: number, data: {
    tipoProducto?: string; enviaACocina: boolean; estacionCocina?: string | null;
    tiempoPreparacionMin?: number | null; diasDisponibles?: number | null; actualizarCosto: boolean;
    componentes: { productoItemId: number; cantidad: number; esOpcional?: boolean }[];
  }) => d<RecetaDetalle>(api.put(`/pos/recetas/${productoId}`, data)),
  datosCocina: (productoId: number, data: { enviaACocina: boolean; estacionCocina?: string | null; tiempoPreparacionMin?: number | null }) =>
    api.put(`/pos/recetas/${productoId}/cocina`, data),
  buscarProductos: (empresaId: number, q?: string, soloVendibles = false, top = 30) =>
    d<ProductoBuscado[]>(api.get('/pos/recetas/buscar-productos', { params: { empresaId, q, soloVendibles, top } })),
};

export const cocinaService = {
  comandas: (sucursalId: number, estacion?: string, entregadasMinutos = 0) =>
    d<Comanda[]>(api.get('/pos/cocina/comandas', { params: { sucursalId, estacion, entregadasMinutos } })),
  estaciones: (empresaId: number) => d<string[]>(api.get('/pos/cocina/estaciones', { params: { empresaId } })),
  turnos: (sucursalId: number) => d<{
    enPreparacion: { numero: number; referencia?: string }[];
    listos: { numero: number; referencia?: string }[];
  }>(api.get('/pos/cocina/turnos', { params: { sucursalId } })),
  cambiarEstado: (comandaId: number, estado: EstadoComanda) =>
    d(api.put(`/pos/cocina/comandas/${comandaId}/estado`, { estado })),
  cambiarEstadoItem: (comandaItemId: number, estado: EstadoComanda) =>
    d(api.put(`/pos/cocina/items/${comandaItemId}/estado`, { estado })),
};

export const mesasService = {
  mapa: (sucursalId: number) => d<MapaMesas>(api.get('/pos/mesas/mapa', { params: { sucursalId } })),
  crearZona: (data: { sucursalId: number; nombre: string; orden?: number }) => d<ZonaMesa>(api.post('/pos/mesas/zonas', data)),
  editarZona: (id: number, data: { sucursalId: number; nombre: string; orden?: number }) => api.put(`/pos/mesas/zonas/${id}`, data),
  eliminarZona: (id: number) => api.delete(`/pos/mesas/zonas/${id}`),
  crear: (data: Partial<MesaMapa> & { sucursalId: number }) => d<MesaMapa>(api.post('/pos/mesas', data)),
  crearLote: (data: { sucursalId: number; zonaMesaId?: number | null; prefijo?: string; desde: number; hasta: number; capacidad: number; forma?: FormaMesa; columnas?: number }) =>
    d<{ creadas: number }>(api.post('/pos/mesas/lote', data)),
  editar: (id: number, data: Partial<MesaMapa> & { sucursalId: number }) => api.put(`/pos/mesas/${id}`, data),
  posiciones: (data: { mesaId: number; zonaMesaId?: number | null; posX: number; posY: number }[]) => api.put('/pos/mesas/posiciones', data),
  eliminar: (id: number) => api.delete(`/pos/mesas/${id}`),
};

export const cuentasService = {
  listar: (sucursalId: number) => d<CuentaResumen[]>(api.get('/pos/cuentas', { params: { sucursalId } })),
  detalle: (cuentaId: number) => d<CuentaDetalle>(api.get(`/pos/cuentas/${cuentaId}`)),
  abrir: (data: { empresaId: number; sucursalId: number; almacenId: number; mesaId?: number | null; nombre?: string; numeroPersonas: number; clienteId?: number; notas?: string; items?: NuevoItemCuenta[] }) =>
    d<CuentaDetalle>(api.post('/pos/cuentas', data)),
  editar: (cuentaId: number, data: { numeroPersonas?: number; nombre?: string; notas?: string; clienteId?: number }) =>
    d<CuentaDetalle>(api.put(`/pos/cuentas/${cuentaId}`, data)),
  agregarItems: (cuentaId: number, items: NuevoItemCuenta[]) =>
    d<CuentaDetalle>(api.post(`/pos/cuentas/${cuentaId}/items`, items)),
  editarItem: (itemId: number, data: { cantidad: number; notas?: string; comensal?: number | null }) =>
    d<CuentaDetalle>(api.put(`/pos/cuentas/items/${itemId}`, data)),
  anularItem: (itemId: number, motivo: string, autorizadoPorId?: string) =>
    d<CuentaDetalle>(api.post(`/pos/cuentas/items/${itemId}/anular`, { motivo, autorizadoPorId })),
  dividirItem: (itemId: number, cantidad: number, comensal?: number) =>
    d<CuentaDetalle>(api.post(`/pos/cuentas/items/${itemId}/dividir`, { cantidad, comensal })),
  enviar: (cuentaId: number) => d<CuentaDetalle>(api.post(`/pos/cuentas/${cuentaId}/enviar`)),
  precuenta: (cuentaId: number) => d<CuentaDetalle>(api.post(`/pos/cuentas/${cuentaId}/precuenta`)),
  reabrir: (cuentaId: number) => d<CuentaDetalle>(api.post(`/pos/cuentas/${cuentaId}/reabrir`)),
  mover: (cuentaId: number, mesaDestinoId: number) =>
    d<CuentaDetalle>(api.post(`/pos/cuentas/${cuentaId}/mover`, { mesaDestinoId })),
  unir: (cuentaId: number, cuentaOrigenId: number) =>
    d<CuentaDetalle>(api.post(`/pos/cuentas/${cuentaId}/unir`, { cuentaOrigenId })),
  anular: (cuentaId: number, motivo: string, autorizadoPorId?: string) =>
    d<CuentaDetalle>(api.post(`/pos/cuentas/${cuentaId}/anular`, { motivo, autorizadoPorId })),
};

/** Texto corto de los modificadores de una línea (igual al de la comanda). */
export const textoModificadores = (mods?: ModificadorItem[] | null) =>
  (mods ?? []).map(m =>
    m.tipoAccion === 'QUITAR'
      ? (/^sin /i.test(m.nombreOpcion) ? m.nombreOpcion : `Sin ${m.nombreOpcion}`)
      : m.tipoAccion === 'AGREGAR' ? `+ ${m.nombreOpcion}` : m.nombreOpcion
  ).join(', ');

// ── Plantillas de negocio (datos de ejemplo completos) ─────────

export interface ResumenPlantilla {
  categorias: number; productosVenta: number; insumos: number; recetas: number;
  platosDelDia: number; modificadores: number; mesas: number; conInventarioInicial: number;
}
export interface PlantillaNegocio { tipo: CodigoTipoNegocio; nombre: string; descripcion: string; resumen: ResumenPlantilla | null; }
export interface ResultadoPlantilla {
  secciones: number; categorias: number; productos: number; recetas: number; modificadores: number;
  zonas: number; mesas: number; inventarioInicial: number; menuVirtual: number;
}

export const plantillasNegocioService = {
  listar: () => d<PlantillaNegocio[]>(api.get('/pos/negocio/plantillas')),
  cargar: (empresaId: number, tipo?: string) =>
    d<ResultadoPlantilla>(api.post(`/pos/negocio/${empresaId}/plantilla`, null, { params: tipo ? { tipo } : undefined })),
};

/** Días en que se vende un producto: lunes=1 … domingo=64 (null = todos los días). */
export const DIAS_SEMANA = [
  { bit: 1, corto: 'L', nombre: 'Lunes' }, { bit: 2, corto: 'M', nombre: 'Martes' },
  { bit: 4, corto: 'X', nombre: 'Miércoles' }, { bit: 8, corto: 'J', nombre: 'Jueves' },
  { bit: 16, corto: 'V', nombre: 'Viernes' }, { bit: 32, corto: 'S', nombre: 'Sábado' },
  { bit: 64, corto: 'D', nombre: 'Domingo' },
];

export const errorApi = (e: any, porDefecto = 'Ocurrió un error') =>
  e?.response?.data?.message ?? e?.message ?? porDefecto;
